import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "components";
import { usePermanentRealTimeTypedQuery } from "components/WebSocket/hooks";
import { typedQueryClient } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, CHECKLIST_TYPES } from "models";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";

import type { WebSocketMessageListener } from "components";
import type { Checklist, ChecklistDealerLocations } from "models";
import type { DropdownItemProps } from "semantic-ui-react";

type ChecklistGroup = {
  name: string;
  checklists: Checklist[];
};

interface GroupedChecklistsDropdownOptions extends DropdownItemProps {
  key: number | string;
  value: number | string;
  text: string;
  disabled?: boolean;
}

const useGetChecklistGroups = () => {
  const { t } = useTranslation();

  const groups = new Map<CHECKLIST_TYPES, ChecklistGroup>([]);
  groups.set(CHECKLIST_TYPES.Regular, { name: t("v8_regular", "Regular"), checklists: [] });
  groups.set(CHECKLIST_TYPES.Warranty, { name: t("v8_warranty", "Warranty"), checklists: [] });
  groups.set(CHECKLIST_TYPES.Info, { name: t("v8_info", "Info"), checklists: [] });
  groups.set(CHECKLIST_TYPES.Quality, { name: t("v8_quality", "Quality"), checklists: [] });
  groups.set(CHECKLIST_TYPES.TestDrive, { name: t("v8_test_drive", "Test Drive"), checklists: [] });

  return groups;
};

export const useGroupedChecklistDropdownOptions = (checklists: Checklist[]) => {
  const groups = useGetChecklistGroups();
  const options: GroupedChecklistsDropdownOptions[] = [];

  checklists.forEach(c => groups.get(c.checklist_type)?.checklists.push(c));
  groups.forEach((group, type) => {
    if (!group.checklists.length) return;

    options.push({ key: `grouped-${type}`, value: `grouped-${type}`, text: group.name, disabled: true });
    group.checklists.sort((a, b) => a.name.localeCompare(b.name));
    group.checklists.forEach(c => options.push({ key: c.id, value: c.id, text: c.name, disabled: false }));
  });

  return options;
};

const filterChecklists = (checklists: Checklist[]) => checklists.filter(c => c.active && !c.is_deleted && ![CHECKLIST_TYPES.ExtraParts].includes(c.checklist_type));

export const useGroupedChecklists = () => {
  const appointmentLocation = useAppointmentLocation();
  const canCallChecklistsEndpoint = useCan([ACL_ACTIVITIES.Create, ACL_ACTIVITIES.Delete, ACL_ACTIVITIES.List, ACL_ACTIVITIES.Update], ACL_RESOURCES.Checklists);

  const locationId = appointmentLocation?.id ?? -1;

  const listeners = useMemo<WebSocketMessageListener<ChecklistDealerLocations>[]>(
    () => [
      {
        model: "ChecklistDealerLocations",
        action: "upsert",
        filter: { dealer_location_id: locationId },
        callback: ({ data: checklistDealerLocation }) => {
          if (locationId < 0) return;

          const attachedChecklist = checklistDealerLocation.checklist;
          if (!attachedChecklist) return;

          const checklists = typedQueryClient.getQueryData(AppointmentDetailsKeys.checklists(locationId)) ?? [];
          const checklistIdx = checklists.findIndex(checklist => checklist.id === attachedChecklist.id);

          const updatedChecklists =
            checklistIdx < 0 ? [...checklists, attachedChecklist] : checklists.with(checklistIdx, { ...checklists[checklistIdx], ...attachedChecklist });

          typedQueryClient.setQueryData(AppointmentDetailsKeys.checklists(locationId), updatedChecklists);
        }
      },
      {
        model: "ChecklistDealerLocations",
        action: "delete",
        filter: { dealer_location_id: locationId },
        callback: ({ data: checklistDealerLocation }) => {
          if (locationId < 0) return;

          const checklists = typedQueryClient.getQueryData(AppointmentDetailsKeys.checklists(locationId));
          if (!checklists?.length) return;

          const checklistIdx = checklists.findIndex(checklist => checklist.id === checklistDealerLocation.checklist_id);
          if (checklistIdx < 0) return;

          typedQueryClient.setQueryData(AppointmentDetailsKeys.checklists(locationId), checklists.toSpliced(checklistIdx, 1));
        }
      }
    ],
    [locationId]
  );

  return usePermanentRealTimeTypedQuery({
    queryKey: AppointmentDetailsKeys.checklists(locationId),
    queryFn: async ({ baseUrl, endpoint }) => (await ApiInstance.post(endpoint, { location_ids: [locationId], embed: "template" }, baseUrl)).data.checklists,
    select: filterChecklists,
    enabled: !!appointmentLocation?.id && canCallChecklistsEndpoint,
    listeners
  });
};
